module.exports = {
    "language": "English",
    "design": {
        "assets": {
            "images": {
                "title": "Images",
                "description": "Writing an article, blog post or feature about Streamr that needs imagery? We've got you covered with a selection of images below, mostly featuring work by our illustrator Stuart Wade. All images at 1600 x 600 pixels.",
                "network": "Network",
                "dataUnions": "Data Unions",
                "marketplace": "Marketplace",
                "core": "Core",
                "techStack": "Tech Stack",
                "smartCity": "Smart City",
                "brand": "Streamr Brand",
                "iot": "Internet of Things",
                "design": "Streamr Design",
                "productDesign": "Streamr Product Design",
                "automotive": "Streamr Automotive Industry",
                "industrial": "Streamr Industrial",
                "renewables": "Streamr Renewables"
            },
            "colours": {
                "title": "Colours",
                "description": "If you’re creating anything based on the Streamr brand, below are the references for the key colours used.",
                "downloadBrandGuide": "Download simple brand guide"
            },
            "typography": {
                "title": "Typography",
                "description": "Streamr’s apps are open source, and so our fonts also need to be. We use Plex Sans and Plex Mono, developed by Bold Monday for IBM. It is quite a large family, with plenty of flexibility and the companion Mono was especially useful to us in redesigning the patch-based UI for the Streamr editor to be more regular and consistent."
            }
        },
        "hero": {
            "description": "Here you’ll find all the design assets for representing Streamr digitally. Everything is provided under a Creative Commons sharealike license. Please try and avoid crimes against whitespace and wild flights of photoshoppery.",
            "title": "Brand assets"
        },
        "logoAssets": {
            "logo": {
                "horizontal-positive-mono-lockup": "Horizontal positive mono lockup",
                "horizontal-colour-lockup": "Horizontal colour lockup",
                "horizontal-reversed-mono-lockup": "Horizontal reversed mono lockup",
                "vertical-positive-mono-lockup": "Vertical positive mono lockup",
                "vertical-colour-lockup": "Vertical colour lockup",
                "vertical-reversed-mono-lockup": "Vertical reversed mono lockup",
                "positive-mono-mark": "Positive mono mark",
                "colour-mark": "Colour mark",
                "reversed-mono-mark": "Reversed mono mark"
            }
        }
    },
    "general": {
        "top": "Top",
        "swash": "Swash",
        "tracey": "Tracey",
        "machineWitness": "Machine Witness",
        "caseStudies": "Case studies",
        "dataToken": "DATA Token",
        "dataUnions": "Data Unions",
        "theNetwork": "The Network",
        "browseMarketplace": "Browse Marketplace",
        "about": "About",
        "builtOnStreamr": {
            "desktop": "Projects built on Streamr",
            "mobile": "Built on Streamr"
        },
        "apps": "Apps",
        "blog": "Blog",
        "build": "Build",
        "community": "Community",
        "design": "Design",
        "designAssets": "Design Assets",
        "docs": "Docs",
        "editor": "Editor",
        "github": "GitHub",
        "home": "Home",
        "marketplace": "Marketplace",
        "medium": "Medium",
        "network": "Network",
        "project": "Project",
        "org": "Org",
        "reddit": "Reddit",
        "trello": "Trello",
        "twitter": "Twitter",
        "gotoMarketplace": "Go to Marketplace",
        "getStarted": "Get Started",
        "youtube": "Youtube",
        "linkedIn": "LinkedIn",
        "mission": "Mission",
        "signIn": "Sign in",
        "signUp": "Sign up",
        "visitTheDocs": "Visit the docs",
        "haveAccountAlready": "Already have an account?",
        "story": "Story",
        "streamrBlog": "Streamr Blog",
        "meetTeam": "Meet the team",
        "platform": "The Platform",
        "networkApps": "Network Apps",
        "learnMore": "Learn more",
        "browseData": "Browse real-time data",
        "createStream": "Create a stream",
        "createProduct": "Create a product",
        "createDataUnion": "Create a data union",
        "marketCap": "Market cap",
        "24hVolume": "24h volume",
        "papers": "Papers",
        "feature": {
            "integrate": {
                "title": "Integrate",
                "description": "Build bridges between existing APIs and Streamr"
            },
            "prototype": {
                "title": "Prototype",
                "description": "Quickly build reactions to real-time data"
            },
            "oracle": {
                "title": "Build Oracles",
                "description": "Easily build rules to interact with Ethereum smart contracts"
            },
            "share": {
                "title": "Share and discover",
                "description": "Tap into a global pool of data streams"
            },
            "monetise": {
                "title": "Monetise",
                "description": "Get paid for the data you produce"
            },
            "crowdsell": {
                "title": "Crowdsell",
                "description": "Incentivize production of previously unavailable data"
            },
            "create": {
                "title": "Create"
            },
            "collaborate": {
                "title": "Collaborate",
                "description": "Share, collaborate and publish your work"
            },
            "analytics": {
                "title": "Analytics",
                "description": "Basic filtering, aggregation and rolling statistics"
            }
        },
        "products": "Products",
        "streams": "Streams",
        "gettingStarted": "Getting Started",
        "whitepaper": "Whitepaper",
        "governanceWhitepaper": "Governance Whitepaper",
        "terms": "Terms & Conditions",
        "privacy": "Privacy"
    },
    "contacts": {
        "general": "General",
        "media": "Media",
        "jobs": "Jobs",
        "business": "Business"
    },
    "top": {
        "hero": {
            "title": "Streamr is the missing real-time data protocol for the decentralized web",
            "description": "Publish and subscribe to cryptographically secure data streams with a few lines of code, trade real-time data on the Marketplace or try Core, our real-time data toolkit.",
            "videoTitle": "Explanatory video"
        },
        "mission": "Streamr’s mission is to build decentralized infrastructure for real-time data, replacing centralized message brokers with a global peer-to-peer network. By relying on cryptography instead of trust, the open-source network enables data sharing and monetization in IoT and smart cities, business consortia, and the decentralized web&nbsp;(Web&nbsp;3.0).",
        "story": "Streamr is a distributed open source project crowdfunded in 2017, with contributors across the world. It was started in reaction to the dominance of the Big Five tech giants, with the core belief that decentralized infrastructure can deliver a healthier distribution of money and power, as well as unlocking brand new use cases.",
        "platform": {
            "codeblockTitle": "Publishing data to a stream",
            "network": {
                "title": "Streamr Network",
                "subtitle": "P2P pub/sub for real-time data",
                "description": "The Streamr Network is a decentralized, scalable publish/subscribe network, designed for low-latency, real-time data from IoT sensors, machines, and other smart devices — a neutral data backbone for the machine data economy. It is simple to work with, the 5 lines of code below show how to send your data to the network."
            },
            "marketplace": {
                "title": "Data Marketplace",
                "subtitle": "Buy & sell real-time data",
                "description": "The Data Marketplace is an open source app built on top&nbsp;of&nbsp;the Network that&nbsp;enables anyone to easily share or&nbsp;monetise real-time data. Using the Core app, you can create data products from sensors and other sources, or build & sell crowdsourced data products with the new Data&nbsp;Unions feature. It can also be forked to enable private&nbsp;instances."
            },
            "dataunion": {
                "title": "Data Unions"
            },
            "userpages": {
                "title": "Userpages"
            },
            "stream": {
                "title": "Stream"
            }
        },
        "token": {
            "headline": "The DATA Token",
            "description": "The DATA token is an ERC-20 token that is used for <a href=\"https://snapshot.org/#/streamr\" target=\"_blank\" rel=\"noopener noreferrer\">project governance</a>, to incentivize the Network, delegating stake on Streamr nodes, and for payments on the Marketplace. It is trading on Uniswap and a range of centralized exchanges. Check out a <a href=\"https://messari.io/asset/streamr/profile\" target=\"_blank\" rel=\"noopener noreferrer\">project profile</a>, <a href=\"https://coinmarketcap.com/currencies/streamr/\" target=\"_blank\" rel=\"noopener noreferrer\">Coinmarketcap</a> listing or the <a href=\"https://etherscan.io/token/{{contractAddress}}\" target=\"_blank\" rel=\"noopener noreferrer\">token contract</a>.",
            "exploreTokenomics": "Explore Tokenomics"
        },
        "blogposts": {
            "title": "News and Dev updates"
        }
    },
    "partners": {
        "caption": "Some of our partners"
    },
    "learn": {
        "network": {
            "hero": {
                "title": "Streamr Network",
                "description": {
                    "desktop": "The Streamr Network is a decentralized, topic-based publish-subscribe system. Each stream or pub-sub topic has its own peer-to-peer overlay network that is built and maintained by a set of BitTorrent-like trackers. Development is still in progress, with Tatum as the current milestone.",
                    "mobile": "The Streamr Network is still under active development, with Tatum as the current major milestone."
                }
            },
            "datapoints": {
                "loading": "Loading live network stats",
                "pointsPerSecond": "Messages per second currently on the Network"
            },
            "usage": {
                "title": "Using the Network",
                "description": {
                    "1": "You can easily use the Network in your own applications via the SDKs or by directly calling the API. Official or community-built SDKs are available for JS, Java and Python, with more in the works.",
                    "2": "These simple code examples show the basics of publishing and subscribing to a stream. For more details, dive into our&nbsp;docs."
                },
                "produce": "Publishing data to a stream",
                "subscribe": "Subscribing to a stream"
            },
            "integrations": {
                "title": "Integrations"
            },
            "whyBuild": {
                "title": "Why build it?",
                "incentivisedScaling": {
                    "title": "Incentivised scaling",
                    "description": "Centralized message brokers have inherent scaling and economic incentive problems. The Streamr Network solves the scaling issue with distributed message brokering over a custom peer-to-peer network architecture. In later milestones, cryptocurrency, cryptographic proofs, and game theory will be used to incentivize people and organizations to run Streamr nodes.<br /><br />Streamr DATAcoin (DATA) is the Ethereum ERC-20 token representing value in the Streamr ecosystem. The companion blockchain is used for value settlement, identity, and permission control, while the data itself stays in the scalable Streamr Network."
                },
                "publishSubscribe": {
                    "title": "Publish/Subscribe",
                    "description": "The pub/sub messaging pattern is a highly useful primitive for data-driven applications, because it decouples data publishers from data subscribers. This simplifies applications and makes them more secure.<br /><br />However, pub/sub is not provided in TCP/IP, the core protocol of the internet, meaning that developers of applications such as instant messengers, financial market data delivery, multiplayer games, or data-driven IoT applications have so far needed to resort to centralized message brokers.<br /><br />This not only creates a single point of failure, but also exposes your precious data to service providers."
                },
                "simpleSetup": {
                    "title": "Simple setup",
                    "description": "For application developers, data publishers simply fire and forget. No need to set up APIs, data silos, or open ports that could compromise security. Just send new data points to the Network and you’re done. This makes integration very easy. For data consumers, they simply listen.<br /><br />There is no need to know where the data sources are, which IP address to reach them on, or how to interface with them. They don’t need to open server ports either. They just connect to the network, subscribe to what they need, and react to incoming messages."
                }
            },
            "howItWorks": {
                "title": "How does it work?",
                "brokerNodes": {
                    "title": "Streamr nodes",
                    "description": "Streamr nodes contribute bandwidth and storage to the network, and earn DATA paid as usage fees by users of the Network. In a sense, running a Streamr node is somewhat comparable to mining, but instead of solving an CPU/GPU-bound artificial problem and wasting energy, nodes provide useful network resources which, using the Streamr protocol, collectively produce the message transport service available to applications. <br /><br />Together, the P2P structure and incentive mechanism enable decentralization, which means that the network can operate without any central party controlling your data or generating value out of it."
                },
                "runningNode": {
                    "title": "Running a node",
                    "description": "The network is a result of many people running Streamr nodes, collectively forming the peer-to-peer network and pub/sub messaging service.<br /><br />The Streamr node is a piece of software you install, intended to be run on always-on machines with stable bandwidth. It connects to other nodes in the network and starts to route data traffic through your computer, and rewards you for it in DATA.<br /><br />We expect to open the network to public untrusted nodes in the Brubeck stage, scheduled for 2021."
                }
            },
            "roadmap": {
                "milestones": "Milestone 1 on Trello"
            }
        },
        "marketplace": {
            "hero": {
                "title": "Streamr Marketplace.<br />Real-time data on demand",
                "description": "The Marketplace dapp provides an easy way to buy, sell and discover data streams, from simple price feeds and sensor data to massive crowdsourced Data Unions with thousands of providers. Buy with DATA, ETH or DAI via Uniswap integration.",
                "browseMarketplace": "Browse Marketplace",
                "marketplaceDocs": "Marketplace Docs"
            },
            "quickstart": {
                "title": "Quickstart Guide",
                "web3": {
                    "title": "Install a Web3 wallet",
                    "description": "MetaMask is the most useful choice to interact with the&nbsp;Marketplace"
                },
                "wallet": {
                    "title": "Get ETH via MyEtherWallet",
                    "description": "If you don’t have any ETH, you can buy some through a&nbsp;MEW&nbsp;integration"
                },
                "protocol": {
                    "title": "Get DATA via Uniswap",
                    "description": "Uniswap is an easy way to swap ETH for&nbsp;DATA",
                    "descriptionDesktop": "Uniswap is an easy way<br />to swap ETH for DATA"
                },
                "connectWallet": "Connect a wallet"
            },
            "usage": {
                "title": "Using the Marketplace",
                "readTheDocs": "Read the Docs",
                "selling": {
                    "title": {
                        "sm": "Selling",
                        "xl": "Selling on the Marketplace"
                    },
                    "description": {
                        "desktop": "On the Streamr Marketplace, you can start monetising any real-time data that you produce or own the rights to. Integrate your data, create a product and start earning DATA. Using the Core app, integrating your data and creating streams is simple.<br /><br />Then just create a product from Core &gt; Products. Set the name, description, category, and the price for your data and you are good to go. The video walks you through the Product Creation flow, and you can learn how to integrate data and create streams here.",
                        "mobile": "On the Streamr marketplace, you can start monetising any real-time data that you produce or own the rights to. The video below walks you through the Product Creation flow, and you can learn how to integrate data and create streams here."
                    }
                },
                "buying": {
                    "title": {
                        "sm": "Buying",
                        "xl": "Buying on the Marketplace"
                    },
                    "description": "Once you have followed the Quickstart guide above, Marketplace purchases are pretty simple. Browse the product tiles, or use the live search to find exactly what you need. View details on the product pages. Make purchases with your wallet. Watch the video for a walkthrough of the purchase process."
                }
            }
        },
        "dataUnions": {
            "requestDemo": {
                "workEmail": "Work email",
                "request": "Request demo",
                "visitTheDocs": "Visit the docs"
            },
            "caseStudies": {
                "title": "Data Unions in use"
            },
            "whyBuild": {
                "title": "Why build a Data Union?"
            },
            "graph": {
                "desktop_caption": "Typical revenue and data flows for a Data Union"
            },
            "getStarted": {
                "mobile_title": "Get started",
                "desktop_title": "Get started with Data Unions"
            }
        }
    },
    "project": {
        "papers": {
            "title": "Streamr Papers",
            "description": "Get access to the range of project-related technical and academic papers the Streamr team have published here, from the original whitepaper to governance, audits and technical ideas. All new publications will be made available on this page.",
            "whitepaper": {
                "title": "Streamr whitepaper",
                "date": "July 25, 2017"
            },
            "lightpaper": {
                "title": "Streamr lightpaper",
                "date": "May 4, 2022"
            },
            "multicastEncryption": {
                "title": "Multicast Encryption",
                "date": "August 22, 2022"
            },
            "networkWhitepaper": {
                "title": "Network whitepaper",
                "date": "August 20, 2020"
            },
            "tokenAudit": {
                "title": "Token sale audit",
                "date": "October 27, 2017"
            },
            "governanceWhitepaper": {
                "title": "Decentralized governance",
                "date": "March 5, 2020"
            },
            "limeChainAudit": {
                "title": "DATAv2 audit (LimeChain)",
                "date": "July 13, 2021"
            },
            "isentropyAudit": {
                "title": "DATAv2 audit (Isentropy)",
                "date": "June 29, 2021"
            },
            "tokenomicsAudit": {
                "title": "Tokenomics audit",
                "date": "November 3, 2023"
            }
        }
    },
    "caseStudy": {
        "readMore": "Read more",
        "readMoreAbout": "Read more about {{study}}",
        "machineWitness": {
            "desc": "As part of the The Trusted IoT Alliance (TIoTA)’s Smart E-Mobility Challenge, Streamr partnered with Bosch Software Innovations and Riddle & Code, on a pilot project that gathered a range of electric vehicle data and&nbsp;shared it openly in real-time. The project was awarded a&nbsp;Silver&nbsp;medal at the TioTA awards."
        },
        "swash": {
            "desc": "A browser extension called Swash has become the world&apos;s first digital Data Union. Introduced at Mozfest in&nbsp;London in October 2019, Swash allows users to capture their browsing data, and then sell it in real-time&nbsp;via a Data Union on the data Marketplace, with one&#8209;to&#8209;many micropayments."
        },
        "tracey": {
            "desc": "A partnership between The World Wide Fund for Nature&nbsp;(WWF) Philippines, UnionBank and Streamr&apos;s consultancy partner TX is working on Tracey, an app for&nbsp;fishermen that will use blockchain for documenting and verifying catch and traceability data. The provided data can then be used by financial institutions such as UnionBank to conduct credit assessments to be able to&nbsp;provide micro loans to the fishermen."
        },
        "paveMotors": {
            "desc": "Pave are building radically simple electric bikes, and rethinking how to move people and their data. From ERC-721 tokens for ownership and bike sharing to light nodes moving metrics on the Streamr Network and unlocking data monetisation for owners, Pave and Streamr are creating the future of personal transport."
        }
    }
};